import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    calculateMaxLoan,
    generateLoanPlans,
} from '@/utils/installment-calculator';

interface UseLoanPlansParams {
    approvedEmi?: number;
    carPrice: number;
    startDate: Date;
    interestRate: number;
    askedDownPayment?: number;
}

const DEFAULT_LOAN_DOWNPAYMENT = 0.5;
const DEFAULT_LOAN_TENOR = 36;

export const useLoanPlans = ({
    approvedEmi,
    carPrice,
    startDate,
    interestRate,
    askedDownPayment = 0,
}: UseLoanPlansParams) => {
    const [tenor, setTenor] = useState<number | null>(null);
    const [downPaymentPercent, setDownPaymentPercent] = useState<number>(0);

    const maxLoanAmount = useMemo(() => {
        return approvedEmi
            ? Math.min(
                  carPrice - askedDownPayment,
                  calculateMaxLoan(approvedEmi, interestRate / 100, startDate)
              )
            : DEFAULT_LOAN_DOWNPAYMENT * carPrice;
    }, [approvedEmi, carPrice, interestRate, startDate]);

    const minDownpaymentPercent = useMemo(
        () => Math.max(carPrice - maxLoanAmount, askedDownPayment),
        [carPrice, maxLoanAmount, askedDownPayment]
    );

    const plans = useMemo(() => {
        if (!carPrice || !downPaymentPercent || !interestRate || !startDate)
            return null;
        return generateLoanPlans(
            approvedEmi,
            carPrice,
            downPaymentPercent / 100,
            interestRate / 100,
            startDate
        );
    }, [approvedEmi, carPrice, downPaymentPercent, interestRate, startDate]);

    const isLoanValidForVehicle = useMemo(
        () =>
            minDownpaymentPercent / carPrice <= 0.95 &&
            minDownpaymentPercent >= askedDownPayment &&
            downPaymentPercent >= 0,
        [minDownpaymentPercent, carPrice, downPaymentPercent]
    );
    const currentPlan = useMemo(
        () => (plans ? plans.plans[tenor ?? 0] : null),
        [plans, tenor]
    );
    const canConfirmPlan =
        !!currentPlan?.installmentAmount &&
        downPaymentPercent >= (100 * minDownpaymentPercent) / carPrice &&
        currentPlan?.installmentAmount > 0;

    useEffect(() => {
        const percent = (100 * minDownpaymentPercent) / carPrice;
        setDownPaymentPercent(percent > 95 ? 0 : percent);
    }, [minDownpaymentPercent, carPrice, interestRate]);

    useEffect(() => {
        if (!plans) return;
        if (plans.minimumDuration === null || tenor > plans.minimumDuration)
            return;
        setTenor(DEFAULT_LOAN_TENOR);
    }, [plans]);

    const handleSetDownPaymentPercent = useCallback(
        (newPercent: number) => {
            if (newPercent !== downPaymentPercent) {
                setDownPaymentPercent(newPercent);
            }
        },
        [downPaymentPercent]
    );

    const handleSetTenor = useCallback(
        (newTenor: number) => {
            if (newTenor !== tenor) {
                setTenor(newTenor);
            }
        },
        [tenor]
    );
    return {
        plans,
        currentPlan,
        tenor,
        setTenor: handleSetTenor,
        downPaymentPercent,
        setDownPaymentPercent: handleSetDownPaymentPercent,
        canConfirmPlan,
        isLoanValidForVehicle,
        maxLoanAmount,
        minDownpaymentPercent,
    };
};
